import type {
  GetEffectiveSettingsRequestParams,
  GetEffectiveSettingsResponse,
  GetSettingDefinitionResponse,
  GetSettingsForScopeRequestParams,
  GetSettingsForScopeResponse,
  RemoveSettingRequestParams,
  SettingName,
  UpsertSettingRequestParams
} from '@gat-ship/types'
import { useGsCloudBackend } from '@gat-ship/http-client'

export const SETTINGS_API_PATHS = {
  getSettingDefinitions: 'settings/definitions',
  getEffectiveSettings: 'settings/effective/batch-get',
  getSettingsForScope: 'settings/scoped/batch-get',
  upsertSetting: (settingName: SettingName) => `settings/scoped/${settingName}`,
  removeSetting: (params: RemoveSettingRequestParams) =>
    `settings/scoped/${params.settingName}`
}

export function useSettingsApiCalls() {
  const { apiGet, apiPost, apiPut, apiDelete } = useGsCloudBackend()

  /** Gets the server-side `/settings/definitions` registry. */
  async function getSettingDefinitionsRequest() {
    return await apiGet<GetSettingDefinitionResponse>(
      SETTINGS_API_PATHS.getSettingDefinitions
    )
  }

  async function getEffectiveSettingsRequest<
    TSettingNames extends readonly SettingName[]
  >(params: GetEffectiveSettingsRequestParams<TSettingNames>) {
    return await apiPost<GetEffectiveSettingsResponse<TSettingNames>>(
      SETTINGS_API_PATHS.getEffectiveSettings,
      params,
      'getEffectiveSettingsRequest'
    )
  }

  async function getSettingsForScopeRequest<
    TSettingNames extends readonly SettingName[]
  >(params: GetSettingsForScopeRequestParams<TSettingNames>) {
    return await apiPost<GetSettingsForScopeResponse<TSettingNames>>(
      SETTINGS_API_PATHS.getSettingsForScope,
      params,
      'getSettingsForScopeRequest'
    )
  }

  async function upsertSettingRequest<TSettingName extends SettingName>(
    params: UpsertSettingRequestParams<TSettingName>
  ) {
    return await apiPut<undefined, UpsertSettingRequestParams<TSettingName>>(
      SETTINGS_API_PATHS.upsertSetting(params.settingName),
      params,
      'upsertSettingRequest'
    )
  }

  async function removeSettingRequest(params: RemoveSettingRequestParams) {
    const queryParams: {
      scope_level: 'global' | 'user' | 'agency'
      agency_node_id?: string
    } = {
      scope_level: params.scopeLevel
    }
    if (params.scopeLevel === 'agency') {
      queryParams['agency_node_id'] = params.agencyNodeId
    }
    return await apiDelete<undefined, RemoveSettingRequestParams>(
      SETTINGS_API_PATHS.removeSetting(params),
      params,
      'removeSettingRequest',
      {
        params: queryParams
      }
    )
  }
  return {
    getSettingDefinitionsRequest,
    getEffectiveSettingsRequest,
    getSettingsForScopeRequest,
    upsertSettingRequest,
    removeSettingRequest
  }
}
